import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useSnackbar } from 'notistack';
import { InvestmentValuationDTO } from '@inversor-hub/types';
import EurCurrencyField from '@/components/form/EurCurrencyField';
import {
  useCreateInvestmentValuation,
  useDeleteInvestmentValuation,
  useListInvestmentValuations,
  useUpdateInvestmentValuation,
} from '@/lib/api/investment-financials';
import { formatCurrency, formatDate, formatDateToYYYYMMDD } from '@/utils/misc';

const valuationSchema = z.object({
  amount: z
    .number({ invalid_type_error: 'Indica el valor de mercado' })
    .int()
    .positive('El valor debe ser mayor que 0'),
  valuationDate: z
    .string()
    .min(1, 'Indica la fecha de la valoración')
    .refine((value) => value <= formatDateToYYYYMMDD(new Date()), 'La fecha no puede ser futura'),
  source: z
    .string()
    .trim()
    .min(1, 'Indica de dónde sale la valoración')
    .max(200, 'Máximo 200 caracteres'),
});

type ValuationFormData = z.infer<typeof valuationSchema>;

const emptyForm = (): ValuationFormData => ({
  amount: 0,
  valuationDate: formatDateToYYYYMMDD(new Date()),
  source: '',
});

interface ValuationsDialogProps {
  open: boolean;
  investmentId: string;
  onClose: () => void;
}

export const ValuationsDialog = ({ open, investmentId, onClose }: ValuationsDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: valuations, isLoading } = useListInvestmentValuations(
    open ? investmentId : undefined
  );
  const createValuation = useCreateInvestmentValuation(investmentId);
  const updateValuation = useUpdateInvestmentValuation(investmentId);
  const deleteValuation = useDeleteInvestmentValuation(investmentId);

  const [editing, setEditing] = useState<InvestmentValuationDTO | undefined>();

  const {
    clearErrors,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ValuationFormData>({
    resolver: zodResolver(valuationSchema),
    defaultValues: emptyForm(),
  });

  const resetForm = useCallback(
    (values: ValuationFormData) => {
      reset(values);
      clearErrors();
    },
    [clearErrors, reset]
  );

  useEffect(() => {
    if (!open) {
      setEditing(undefined);
      resetForm(emptyForm());
    }
  }, [open, resetForm]);

  const startEditing = (valuation: InvestmentValuationDTO) => {
    setEditing(valuation);
    resetForm({
      amount: valuation.amount,
      valuationDate: valuation.valuationDate,
      source: valuation.source,
    });
  };

  const cancelEditing = () => {
    setEditing(undefined);
    resetForm(emptyForm());
  };

  const onSubmit = async (data: ValuationFormData) => {
    try {
      if (editing) {
        await updateValuation.mutateAsync({ valuationId: editing.id, data });
        enqueueSnackbar('Valoración actualizada', { variant: 'success' });
      } else {
        await createValuation.mutateAsync(data);
        enqueueSnackbar('Valoración añadida', { variant: 'success' });
      }
      cancelEditing();
    } catch (_error) {
      enqueueSnackbar('No se pudo guardar la valoración', { variant: 'error' });
    }
  };

  const handleDelete = async (valuation: InvestmentValuationDTO) => {
    if (!window.confirm(`¿Eliminar la valoración de ${formatDate(valuation.valuationDate)}?`)) {
      return;
    }
    try {
      await deleteValuation.mutateAsync(valuation.id);
      if (editing?.id === valuation.id) cancelEditing();
      enqueueSnackbar('Valoración eliminada', { variant: 'success' });
    } catch (_error) {
      enqueueSnackbar('No se pudo eliminar la valoración', { variant: 'error' });
    }
  };

  const isSubmitting = createValuation.isPending || updateValuation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Valoraciones de mercado</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2.5 }}>
          La rentabilidad sobre valor actual usa la valoración más reciente anterior al corte del
          periodo. Guarda el histórico para poder comparar ejercicios.
        </Alert>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {editing ? 'Editar valoración' : 'Nueva valoración'}
          </Typography>
          <Stack spacing={2}>
            <Controller
              name="amount"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Valor de mercado"
                  value={value || null}
                  onChange={(next) => onChange(next ?? 0)}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                />
              )}
            />
            <Controller
              name="valuationDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fecha de la valoración"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.valuationDate}
                  helperText={errors.valuationDate?.message}
                />
              )}
            />
            <Controller
              name="source"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Origen"
                  fullWidth
                  placeholder="Tasación homologada, portal inmobiliario, agencia…"
                  error={!!errors.source}
                  helperText={errors.source?.message ?? 'De dónde sale este valor'}
                />
              )}
            />
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {editing ? 'Guardar cambios' : 'Añadir valoración'}
              </Button>
              {editing && (
                <Button variant="text" onClick={cancelEditing} disabled={isSubmitting}>
                  Cancelar
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Histórico
        </Typography>
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : !valuations || valuations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Todavía no has registrado ninguna valoración.
          </Typography>
        ) : (
          <List dense disablePadding>
            {valuations.map((valuation) => (
              <ListItem
                key={valuation.id}
                disableGutters
                divider
                secondaryAction={
                  <Box>
                    <IconButton
                      size="small"
                      aria-label={`Editar valoración de ${formatDate(valuation.valuationDate)}`}
                      onClick={() => startEditing(valuation)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Eliminar valoración de ${formatDate(valuation.valuationDate)}`}
                      onClick={() => handleDelete(valuation)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={`${formatCurrency(valuation.amount)} · ${formatDate(valuation.valuationDate)}`}
                  secondary={valuation.source}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};
